import React, { useState } from 'react';
import { Tabs, Tab, Box, Card, CardContent, Button } from '@mui/material';
import SeminalFluidAnalysis from './seminalFluidAnalysis';
import PatientDetails from './patientDetails';
import SemenAnalysis from './semenAnalysis';
import CyroFreezing from './cyroFreezing';
import OtherDetails from './otherDetails';
import { styled } from '@mui/system';
import {
  createSFRReport,
  generateSFRReport,
  updateSFRReport,
} from '../../../../api/andrologyApi';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetAndrologyData,
  setAndrologyData,
} from '../../../../actions/andrologyAction';
import {
  checkFileAvailabilityApi,
  getPatientDetailsApi,
} from '../../../../api/patientApi';
import { pollFileAvailability } from '../../../../api/utils';
import { toast } from 'sonner';
import axios from 'axios';
import UIModal from '../../../../components/UIElements/UIModal';

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  borderRadius: '8px',
}));

const StyledBox = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3),
  border: '1px solid #ddd',
  borderRadius: '8px',
}));

const SfrReport = ({ setIsReportSelected }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleNext = () => {
    if (selectedTab < 4) {
      setSelectedTab((prev) => prev + 1);
    }
  };

  const handleSaveAndNext = () => {
    if (!sfrData.id) {
      handleSave();
    } else {
      handleUpdate();
    }
    handleNext();
  };

  const handleBack = () => {
    if (selectedTab > 0) {
      setSelectedTab((prev) => prev - 1);
    }
  };

  const selectedPatientData = useSelector(
    (state) => state.andrologyReducer.selectedPatientData,
  );
  const sfrData = useSelector((state) => state.andrologyReducer.sfr);

  const dispatch = useDispatch();
  const handleSave = async () => {
    setButtonsDisabled(true);
    const res = await createSFRReport(selectedPatientData.patient.id, {
      ...sfrData,
      id: undefined,
    });
    dispatch(setAndrologyData('sfr', res.data));
    setButtonsDisabled(false);
  };

  const handleUpdate = async () => {
    setButtonsDisabled(true);
    const res = await updateSFRReport(selectedPatientData.patient.id, sfrData);
    dispatch(setAndrologyData('sfr', res.sfr));
    setButtonsDisabled(false);
  };

  const [isDownloadDisabled, setIsDownloadDisabled] = useState(false);
  const handleGenerateReport = async () => {
    setIsDownloadDisabled(true);
    try {
      const res = await generateSFRReport(
        selectedPatientData.patient.id,
        sfrData.id,
      );
      // dispatch(resetAndrologyData());

      if (res && res.pdf && res.pdf.file) {
        toast.success('Report Generated');
        dispatch(setAndrologyData('refresh', true));
        const presignedUrl = await pollFileAvailability(
          checkFileAvailabilityApi,
          res.pdf.file.bucketName,
          res.pdf.file.fileKey,
        );
        dispatch(setAndrologyData('sfr.reportId', res.pdf.file.id));
        downloadFile(presignedUrl, selectedPatientData.patient.id);
        setIsDownloadDisabled(false);
        toast.success('Report is downloaded successfully');
      }
    } catch (error) {
      console.error('Failed to get presigned URL:', error);
    } finally {
      setIsDownloadDisabled(false);
      setButtonsDisabled(false);
    }
  };

  const downloadFile = async (presignedUrl, patientId) => {
    try {
      const response = await axios.get(presignedUrl, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${patientId}-sfr-report.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading the file', error);
    }
  };

  const handleCancelStartReport = () => {
    setOpenConfirmModal(false);
  };

  return (
    <div className="theme-blush">
      <StyledCard>
        <CardContent>
          <StyledBox>
            <Tabs
              value={selectedTab}
              onChange={handleChange}
              aria-label="SFR Report Tabs"
            >
              <Tab label="Patient Details" />
              <Tab label="Seminal Fluid Analysis" />
              <Tab label="Semen Analysis" />
              <Tab label="Cryo Freezing" />
              <Tab label="Other Details" />
            </Tabs>
            <Box mt={2}>
              {selectedTab === 0 && (
                <PatientDetails keyPath={'sfr.patientDetails'} />
              )}
              {selectedTab === 1 && (
                <SeminalFluidAnalysis keyPath={'sfr.seminalFluidAnalysis'} />
              )}
              {selectedTab === 2 && (
                <SemenAnalysis keyPath={'sfr.spermAnalysis'} />
              )}
              {selectedTab === 3 && (
                <CyroFreezing keyPath={'sfr.cryopreservation'} />
              )}
              {selectedTab === 4 && (
                <OtherDetails keyPath={'sfr.otherDetails'} />
              )}
            </Box>
            <Box
              mt={4}
              display={'flex'}
              flexDirection={'row'}
              gap={'12px'}
              justifyContent={'end'}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setIsReportSelected(false);
                }}
                style={{ minWidth: '180px' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleBack}
                disabled={selectedTab === 0}
                style={{ minWidth: '180px' }}
              >
                Back
              </Button>
              {selectedTab != 4 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveAndNext}
                  style={{ minWidth: '180px' }}
                >
                  Save And Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveAndNext}
                  style={{ minWidth: '180px' }}
                >
                  Save
                </Button>
              )}
              {selectedTab == 4 && (
                <Button
                  variant="contained"
                  color="primary"
                  style={{ minWidth: '180px' }}
                  onClick={() => setOpenConfirmModal(true)}
                  disabled={isDownloadDisabled || buttonsDisabled}
                >
                  Generate Report
                </Button>
              )}
            </Box>
          </StyledBox>
        </CardContent>
      </StyledCard>
      <UIModal
        title="Please confirm?"
        body="Do you want to generate report? Report once generated can't be changed. Do you still want to go ahead?"
        proceedText="Confirm"
        cancelText="Cancel"
        show={openConfirmModal}
        onClose={() => handleCancelStartReport()}
        onSave={() => {
          handleGenerateReport();
          setOpenConfirmModal(false);
        }}
      />
    </div>
  );
};

export default SfrReport;
